import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Collapse,
  Dialog,
  Skeleton,
  Typography,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ErrorOutline as ErrorOutlineIcon,
  Terminal as TerminalIcon,
} from '@mui/icons-material';
import OpenCodeChatService from '../../services/OpenCodeChatService';

const TOOL_STATUS_COLORS = {
  pending: 'text.secondary',
  running: 'info.main',
  completed: 'text.secondary',
  error: 'error.main',
};

export function ChatImage({ chatId, image, onOpen, width = { xs: '100%', sm: 300 } }) {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let objectUrl = '';
    let active = true;
    OpenCodeChatService.image(chatId, image.id)
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => { if (active) setFailed(true); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [chatId, image.id]);

  return (
    <Box sx={{ width }}>
      <Box
        sx={{ aspectRatio: '16 / 9', borderRadius: '10px', overflow: 'hidden', bgcolor: '#eceef6', cursor: url ? 'zoom-in' : 'default' }}
        onClick={() => url && onOpen({ url, path: image.path })}
      >
        {url ? (
          <Box component="img" src={url} alt={image.path} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        ) : failed ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'grid', placeItems: 'center', height: '100%' }}>
            Image unavailable
          </Typography>
        ) : (
          <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%' }} />
        )}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{image.path}</Typography>
    </Box>
  );
}

function ToolCall({ part }) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ my: 0.5 }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((value) => !value)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          p: 0,
          border: 0,
          background: 'none',
          cursor: 'pointer',
          color: TOOL_STATUS_COLORS[part.status] || 'text.secondary',
          font: 'inherit',
          textAlign: 'left',
          maxWidth: '100%',
        }}
      >
        <ChevronRightIcon fontSize="small" sx={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
        {part.status === 'running' || part.status === 'pending'
          ? <CircularProgress size={12} />
          : <TerminalIcon sx={{ fontSize: 15 }} />}
        <Typography variant="caption" sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {part.tool}{part.title ? `: ${part.title}` : ''}
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box
          component="pre"
          sx={{
            m: 0, mt: 0.5, p: 1.25, borderRadius: '8px', bgcolor: '#1d2030', color: '#e7e9f3', fontSize: 12,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 360, overflow: 'auto',
          }}
        >
          {part.input}
          {part.output ? `\n\n${part.output}` : ''}
        </Box>
      </Collapse>
    </Box>
  );
}

// The model's reasoning, collapsed to one line until opened.
function Reasoning({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <Box
      component="button"
      type="button"
      onClick={() => setOpen((value) => !value)}
      sx={{ display: 'block', width: '100%', p: 0, my: 0.5, border: 0, background: 'none', cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          fontStyle: 'italic',
          whiteSpace: open ? 'pre-wrap' : 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {open ? text : `Thinking: ${text.replace(/\s+/g, ' ')}`}
      </Typography>
    </Box>
  );
}

function AssistantMessage({ message }) {
  return (
    <Box sx={{ alignSelf: 'flex-start', maxWidth: '100%', width: '100%' }}>
      {message.parts.map((part, index) => {
        // A message's parts never reorder, so their position is a stable key.
        if (part.type === 'tool') return <ToolCall key={index} part={part} />;
        if (part.type === 'reasoning') return <Reasoning key={index} text={part.text.trim()} />;
        return (
          <Typography key={index} variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', my: 0.75 }}>
            {part.text.trim()}
          </Typography>
        );
      })}
      {message.error && (
        <Typography variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, my: 0.75 }}>
          <ErrorOutlineIcon fontSize="small" /> {message.error}
        </Typography>
      )}
    </Box>
  );
}

function UserMessage({ message }) {
  const text = message.parts.filter((part) => part.type === 'text').map((part) => part.text).join('\n');
  return (
    <Box sx={{ alignSelf: 'flex-end', maxWidth: '80%', px: 1.75, py: 1.25, borderRadius: '14px 14px 4px 14px', bgcolor: 'primary.main', color: 'white' }}>
      <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</Typography>
    </Box>
  );
}

// Messages and images in the order they happened. Images in a row are grouped so
// they are shown side by side.
function timeline(chat) {
  const items = [
    ...chat.messages.map((message) => ({ kind: 'message', key: message.id, time: message.createdAt || 0, message })),
    ...chat.images.map((image) => ({ kind: 'image', key: image.id, time: new Date(image.createdAt).getTime(), image })),
  ].sort((a, b) => a.time - b.time);
  return items.reduce((grouped, item) => {
    const last = grouped[grouped.length - 1];
    if (item.kind === 'image' && last?.kind === 'images') last.images.push(item.image);
    else if (item.kind === 'image') grouped.push({ kind: 'images', key: item.key, images: [item.image] });
    else grouped.push(item);
    return grouped;
  }, []);
}

/** The chat's conversation: the person's messages, OpenCode's replies and tool calls, and images. */
export default function ChatTranscript({ chat }) {
  const [enlarged, setEnlarged] = useState(null);
  const items = timeline(chat);

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {items.map((item) => {
          if (item.kind === 'images') {
            return (
              <Box key={item.key} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {item.images.map((image) => (
                  <ChatImage key={image.id} chatId={chat.id} image={image} onOpen={setEnlarged} />
                ))}
              </Box>
            );
          }
          return item.message.role === 'user'
            ? <UserMessage key={item.key} message={item.message} />
            : <AssistantMessage key={item.key} message={item.message} />;
        })}
      </Box>
      <Dialog open={Boolean(enlarged)} onClose={() => setEnlarged(null)} maxWidth="lg">
        {enlarged && (
          <Box component="img" src={enlarged.url} alt={enlarged.path} sx={{ display: 'block', maxWidth: '100%' }} />
        )}
      </Dialog>
    </>
  );
}
